import { useSharedAPI } from "@certn/api";
import { useMemo, FocusEvent } from "react";

import { SearchableSelect } from "../../components/inputs/select/SearchableSelect";
import { SelectItem } from "../../components/inputs/select/SelectItem";

import { FieldErrorsProps } from "../../components/inputs/_assets/input-wrapper/FieldErrors";

// Common countries to show at the top
const COMMON_COUNTRIES = [
  "CA", // Canada
  "US", // United States
  "GB", // United Kingdom
  "IN", // India
  "PH", // Philippines
  "AU", // Australia
];

type CountrySelectProps = {
  label: string;
  isRequired?: boolean;
  testID: string;
  onChange?: (key: string) => void;
  onFilter?: (country: { alpha_2: string; name: string }) => boolean;
  selectedKey?: string;
  errors?: FieldErrorsProps["children"];
  isInvalid?: boolean;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  isDisabled?: boolean;
  description?: string;
  info?: React.ReactNode;
};

export function CountrySelect(props: CountrySelectProps) {
  const {
    label,
    isRequired,
    testID,
    onChange,
    onFilter,
    selectedKey,
    description,
    info,
  } = props;

  const { data: countryList, isLoading } = useSharedAPI(
    "/api/internal/shared/countries/"
  );

  const sortedCountryList = useMemo(
    () =>
      countryList?.sort((a, b) => {
        // Check if either country is in the common list
        const aIsCommon = COMMON_COUNTRIES.includes(a.alpha_2);
        const bIsCommon = COMMON_COUNTRIES.includes(b.alpha_2);

        // If one is common and the other isn't, the common one comes first
        if (aIsCommon && !bIsCommon) return -1;
        if (!aIsCommon && bIsCommon) return 1;

        // If both are common, sort by their position in the commonCountries array
        if (aIsCommon && bIsCommon) {
          return (
            COMMON_COUNTRIES.indexOf(a.alpha_2) -
            COMMON_COUNTRIES.indexOf(b.alpha_2)
          );
        }

        // If neither is common, sort alphabetically
        return a.name.localeCompare(b.name);
      }),
    [countryList]
  );

  const filteredCountryList = useMemo(
    () =>
      sortedCountryList?.filter((country) =>
        onFilter ? onFilter(country) : true
      ),
    [sortedCountryList, onFilter]
  );

  return (
    <SearchableSelect
      isInvalid={props.isInvalid}
      errors={props.errors}
      menuTrigger="focus"
      testID={testID}
      data-testid={`${testID}-wrapper`}
      label={label}
      isRequired={isRequired}
      isDisabled={isLoading || !filteredCountryList?.length || props.isDisabled}
      // without setTimeout, app renders infinitely when clearing the input
      onSelectionChange={(key) => setTimeout(() => onChange?.(key as string))}
      selectedKey={selectedKey ?? ""}
      isLoading={isLoading}
      items={filteredCountryList}
      onBlur={props.onBlur}
      description={description}
      info={info}
    >
      {(item) => (
        <SelectItem key={item.alpha_2} id={item.alpha_2} textValue={item.name}>
          {item.name}
        </SelectItem>
      )}
    </SearchableSelect>
  );
}
